import { FC } from 'react';

import Paragraph from '@/components/text/Paragraph.tsx';

import { PlacedAppointment } from '@/models/appointment/Appointment.ts';

import { useBookingStore } from '@/stores/bookingStore.ts';
import { useCalendarStore } from '@/stores/calendarStore.ts';

import { calculateAppointmentHeight, calculateAppointmentTop } from '@/utils/calendarUtils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';

interface Props {
    placedAppointment: PlacedAppointment;
    containerHeight: number;
    timeslotHeight: number;
}

const SkeletonAppointmentCard: FC<Props> = ({ placedAppointment, containerHeight, timeslotHeight }) => {
    const timelineInterval = useCalendarStore((state) => state.timeline_interval);
    const headerHeight = useCalendarStore((state) => state.header_height);
    const seriesPlacementConfig = useBookingStore((state) => state.seriesPlacementConfig);

    const totalAppointments = seriesPlacementConfig?.totalAppointments ?? 0;
    const patientName = seriesPlacementConfig?.patientName ?? '';
    const durationMinutes = seriesPlacementConfig?.durationMinutes ?? 30;

    const baseTopPosition = calculateAppointmentTop(
        placedAppointment.startDatetime,
        containerHeight,
        timeslotHeight,
        timelineInterval
    );
    const headerHeightPercentage = headerHeight ? (headerHeight / containerHeight) * 100 : 0;
    const topPosition = baseTopPosition + headerHeightPercentage;

    const heightPercentage = calculateAppointmentHeight(
        durationMinutes,
        containerHeight,
        timeslotHeight,
        timelineInterval
    );

    const timeLabel = dayjs(placedAppointment.startDatetime).format('HH:mm');

    return (
        <div
            className="absolute left-0 z-[1] w-[98%] overflow-hidden rounded border-2 border-dashed border-primary bg-primary/10 px-2 py-1"
            style={{
                top: `${topPosition}%`,
                height: `${heightPercentage}%`,
            }}
        >
            <div className="flex items-center gap-1.5 text-xs">
                <span className="shrink-0 rounded bg-primary px-1 py-0.5 text-[10px] font-semibold leading-none text-white">
                    {placedAppointment.position}/{totalAppointments}
                </span>
                <Paragraph className="shrink-0 font-medium">{timeLabel}</Paragraph>
                <Paragraph className="truncate">{patientName}</Paragraph>
                <Paragraph className="shrink-0 text-slate-500">Vorläufig</Paragraph>
            </div>
        </div>
    );
};

export default SkeletonAppointmentCard;
